import { useState } from 'react'
import DOMPurify from 'dompurify'
import { formatSpellLevel, formatComponents } from '../../lib/spellSchools'
import { useSpellSchools } from '../../hooks/useSpellSchools'
import './SpellCard.css'

/**
 * PHB-style spell block display component.
 */
export default function SpellCard({ spell, showExport = true }) {
  const [exporting, setExporting] = useState(false)
  const { getSchoolName, getSchoolIcon, getSchool } = useSpellSchools()

  const handleExport = async () => {
    setExporting(true)
    try {
      // Dynamic import to reduce bundle size
      const { downloadSingleSpellPdf } = await import('../../lib/spellPdf.jsx')
      await downloadSingleSpellPdf(spell)
    } catch (err) {
      console.error('Failed to export PDF:', err)
    } finally {
      setExporting(false)
    }
  }

  if (!spell) return null

  const schoolName = getSchoolName(spell.school)
  const schoolIcon = getSchoolIcon(spell.school)
  const school = getSchool(spell.school)

  const levelSchool = spell.level === 0
    ? `${schoolName} cantrip`
    : `${formatSpellLevel(spell.level)} ${schoolName.toLowerCase()}`

  const sanitizedDescription = DOMPurify.sanitize(spell.description || '')
  const sanitizedHigherLevels = DOMPurify.sanitize(spell.atHigherLevels || '')

  return (
    <div className="spell-card">
      <div className="spell-header">
        <div className="spell-title-row">
          <h3 className="spell-name">{spell.name}</h3>
          <span className="spell-school-icon" title={schoolName}>
            {schoolIcon}
          </span>
        </div>
        <p className="spell-level-school">
          <em>{levelSchool}</em>
          {school?.unofficial && <span className="spell-unofficial-badge">Unofficial</span>}
        </p>
      </div>

      <div className="spell-stats">
        <div className="spell-stat">
          <span className="stat-label">Casting Time:</span>
          <span className="stat-value">{spell.castingTime || '1 action'}</span>
        </div>
        <div className="spell-stat">
          <span className="stat-label">Range:</span>
          <span className="stat-value">{spell.range || 'Self'}</span>
        </div>
        <div className="spell-stat">
          <span className="stat-label">Components:</span>
          <span className="stat-value">{formatComponents(spell.components) || 'V, S'}</span>
        </div>
        <div className="spell-stat">
          <span className="stat-label">Duration:</span>
          <span className="stat-value">
            {spell.concentration && 'Concentration, up to '}
            {spell.duration || 'Instantaneous'}
          </span>
        </div>
      </div>

      <div className="spell-divider" />

      <div
        className="spell-description"
        dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
      />

      {sanitizedHigherLevels && sanitizedHigherLevels !== '<p><br></p>' && (
        <div className="spell-higher-levels">
          <span className="higher-levels-label">At Higher Levels. </span>
          <span dangerouslySetInnerHTML={{ __html: sanitizedHigherLevels }} />
        </div>
      )}

      {showExport && (
        <div className="spell-actions">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? 'Exporting...' : 'Export PDF'}
          </button>
        </div>
      )}
    </div>
  )
}
